import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Shield, AlertTriangle, CheckCircle2, RotateCcw, Clock,
  MapPin, Phone, Globe, Tag, Building2, Image, FileText,
  CalendarDays, Check, X, Loader2, Bell, Eye,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, ListingChange, ListingSnapshot } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

const MONITORED_FIELDS = [
  { key: "name", label: "Business Name", icon: Building2 },
  { key: "address", label: "Address", icon: MapPin },
  { key: "phone", label: "Phone Number", icon: Phone },
  { key: "hours", label: "Business Hours", icon: Clock },
  { key: "category", label: "Primary Category", icon: Tag },
  { key: "website", label: "Website URL", icon: Globe },
  { key: "description", label: "Business Description", icon: FileText },
  { key: "photos", label: "Photos", icon: Image },
];

function HeroBanner({ hasIssues, lastChecked, unprotected }: { hasIssues: boolean; lastChecked: string; unprotected?: boolean }) {
  if (unprotected) {
    return (
      <Card className="border-2 border-red-500/50 bg-red-500/5" data-testid="card-hero-unprotected">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center animate-pulse">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-red-500" data-testid="text-hero-status">
                YOUR LISTING IS NOT PROTECTED
              </h2>
              <p className="text-sm text-foreground/90 mt-1">
                We are <strong>not</strong> monitoring your Google Business listing. If a competitor hijacks your address, swaps your phone, edits your hours, or changes your category — <strong>you will not be alerted</strong>.
              </p>
              <p className="text-xs text-red-500/80 mt-2 flex items-center gap-1 font-medium">
                <X className="w-3 h-3" />
                Last scan: never
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className={hasIssues ? "border-brand-orange/30" : "border-emerald-500/30"}>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            hasIssues ? "bg-brand-orange/10" : "bg-emerald-500/10"
          }`}>
            {hasIssues ? (
              <AlertTriangle className="w-8 h-8 text-brand-orange" />
            ) : (
              <Shield className="w-8 h-8 text-emerald-500" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold" data-testid="text-hero-status">
              {hasIssues ? "Changes Detected" : "All Clear"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {hasIssues
                ? "Review required — some listing fields may have been modified"
                : "No unauthorized changes detected"}
            </p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Last scanned {lastChecked}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProtectedFieldsCard({
  snapshots,
  onReview,
  unprotected,
}: {
  snapshots: ListingSnapshot[];
  onReview: (s: ListingSnapshot) => void;
  unprotected?: boolean;
}) {
  if (unprotected) {
    return (
      <Card className="border-red-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <span>Unprotected Fields</span>
            <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px] py-0">AT RISK</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {MONITORED_FIELDS.map(({ key, label, icon: Icon }) => (
            <div
              key={key}
              className="flex items-center justify-between p-2.5 rounded-md bg-red-500/5 border border-red-500/15"
              data-testid={`field-${key}`}
            >
              <div className="flex items-center gap-2.5">
                <Icon className="w-4 h-4 text-red-500/70" />
                <span className="text-sm text-foreground/80">{label}</span>
              </div>
              <div className="flex items-center gap-1 text-red-500">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs font-medium">Unmonitored</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
  const pendingByField = new Map<string, ListingSnapshot>();
  snapshots.filter(s => s.status === "pending").forEach(s => {
    if (!pendingByField.has(s.fieldName)) pendingByField.set(s.fieldName, s);
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Protected Fields</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {MONITORED_FIELDS.map(({ key, label, icon: Icon }) => {
          const issue = pendingByField.get(key);
          return (
            <div
              key={key}
              className="flex items-center justify-between p-2.5 rounded-md bg-muted/30"
              data-testid={`field-${key}`}
            >
              <div className="flex items-center gap-2.5">
                <Icon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{label}</span>
              </div>
              {issue ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-brand-orange text-brand-orange text-xs h-7"
                  onClick={() => onReview(issue)}
                  data-testid={`button-review-${key}`}
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Review
                </Button>
              ) : (
                <div className="flex items-center gap-1 text-emerald-500">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs font-medium">Verified</span>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ReviewModal({
  snapshot,
  onClose,
}: {
  snapshot: ListingSnapshot | null;
  onClose: () => void;
}) {
  const { toast } = useToast();

  const resolveMutation = useMutation({
    mutationFn: async (status: string) => {
      await apiRequest("POST", "/api/listing/resolve", { id: snapshot!.id, status });
    },
    onSuccess: (_, status) => {
      queryClient.invalidateQueries({ queryKey: ["/api/listing/snapshots"] });
      toast({ title: status === "restored" ? "Field restored" : "Change accepted" });
      onClose();
    },
  });

  if (!snapshot) return null;

  return (
    <Dialog open={!!snapshot} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review Change: {snapshot.fieldName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20">
              <p className="text-xs font-medium text-emerald-500 mb-1">Original Value</p>
              <p className="text-sm">{snapshot.expectedValue}</p>
            </div>
            <div className="p-3 rounded-md bg-brand-orange/5 border border-brand-orange/20">
              <p className="text-xs font-medium text-brand-orange mb-1">Current Value</p>
              <p className="text-sm">{snapshot.currentValue}</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => resolveMutation.mutate("accepted")}
              disabled={resolveMutation.isPending}
              data-testid="button-accept-change"
            >
              <Check className="w-3 h-3 mr-1" />
              Accept Change
            </Button>
            <Button
              size="sm"
              onClick={() => resolveMutation.mutate("restored")}
              disabled={resolveMutation.isPending}
              data-testid="button-restore-field"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Restore
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChangeHistoryFeed({ snapshots, unprotected }: { snapshots: ListingSnapshot[]; unprotected?: boolean }) {
  if (unprotected) {
    return (
      <Card className="border-red-500/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Change History</CardTitle>
            <Badge className="bg-red-500/15 text-red-500 border-red-500/30">Blind</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-sm font-medium text-red-500">Monitoring not active</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-[260px]">
              We can't tell you what's changed on your Google Business listing because we aren't watching it. Activate protection to start tracking edits.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "outline"; label: string }> = {
    restored: { variant: "default", label: "Restored" },
    accepted: { variant: "secondary", label: "Accepted" },
    pending: { variant: "outline", label: "Pending" },
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Change History</CardTitle>
          <Badge variant="outline">{snapshots.length} total</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {snapshots.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
              <Shield className="w-6 h-6 text-emerald-500" />
            </div>
            <p className="text-sm font-medium">No Changes Detected</p>
            <p className="text-xs text-muted-foreground mt-1">
              No changes have been detected since monitoring began.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {snapshots.map((s) => {
              const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
              return (
                <div
                  key={s.id}
                  className="flex items-center justify-between p-3 rounded-md bg-muted/30"
                  data-testid={`history-item-${s.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{s.fieldName}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      "{s.expectedValue}" → "{s.currentValue}"
                    </p>
                    {s.detectedAt && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(s.detectedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Badge variant={badge.variant} className={
                    s.status === "restored" ? "bg-emerald-500/10 text-emerald-500" :
                    s.status === "pending" ? "bg-brand-orange/10 text-brand-orange" : ""
                  }>
                    {badge.label}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BuyProtectionBanner({ locationId, hasGbp }: { locationId: string; hasGbp: boolean }) {
  const { toast } = useToast();
  const checkout = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/citation-orders/checkout", {
        locationId,
        packageType: "listing_protection",
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.url) window.location.href = data.url;
    },
    onError: (err: any) => {
      toast({ title: "Checkout failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Card className="border-2 border-brand-orange/40 bg-gradient-to-br from-brand-orange/5 to-amber-500/5" data-testid="card-buy-protection">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-orange to-amber-500 flex items-center justify-center shrink-0 shadow-lg shadow-brand-orange/20">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold" data-testid="text-paywall-title">Protect this listing — $10/mo</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                24/7 monitoring of every field on your Google Business Profile. Get instant alerts the second someone hijacks your address, swaps your phone, or edits your category.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Hourly checks</span>
                <span className="flex items-center gap-1"><Bell className="w-3 h-3" /> Instant alerts</span>
                <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Full change history</span>
              </div>
            </div>
          </div>
          <Button
            size="lg"
            onClick={() => checkout.mutate()}
            disabled={!hasGbp || checkout.isPending}
            className="bg-brand-orange hover:bg-brand-orange/90 text-white shadow-lg shadow-brand-orange/20 shrink-0"
            data-testid="button-buy-protection"
          >
            {checkout.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
            {hasGbp ? "Activate Protection" : "Link a GBP first"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ListingPage() {
  const { selectedLocationId } = useLocationStore();
  const [reviewSnapshot, setReviewSnapshot] = useState<ListingSnapshot | null>(null);

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;
  const currentLocation = locations?.find((l) => l.id === locationId);

  const { data: guard } = useQuery<{ active: boolean; reportKey?: string | null }>({
    queryKey: ["/api/locations", locationId, "falcon-guard"],
    enabled: !!locationId,
  });
  const isProtected = !!guard?.active && !!guard?.reportKey;

  const { data: changes = [] } = useQuery<ListingChange[]>({
    queryKey: ["/api/listing-changes", locationId],
    enabled: !!locationId,
  });

  const { data: snapshots = [], isLoading } = useQuery<ListingSnapshot[]>({
    queryKey: ["/api/listing/snapshots", locationId],
    enabled: !!locationId,
  });

  const pendingCount = snapshots.filter(s => s.status === "pending").length;
  const restoredCount = snapshots.filter(s => s.status === "restored").length;
  const daysProtected = snapshots.length > 0
    ? Math.max(1, Math.floor((Date.now() - new Date(snapshots[snapshots.length - 1].detectedAt || Date.now()).getTime()) / 86400000))
    : currentLocation ? 1 : 0;

  if (isLoading || !currentLocation) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full" data-testid="page-listing">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-listing-title">
          Listing Protection
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor and protect your business listing data
        </p>
      </div>

      {!isProtected && locationId && (
        <BuyProtectionBanner locationId={locationId} hasGbp={!!currentLocation?.gbpPlaceId} />
      )}

      <HeroBanner
        hasIssues={pendingCount > 0}
        lastChecked="2 hours ago"
        unprotected={!isProtected}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ProtectedFieldsCard
          snapshots={snapshots}
          onReview={(s) => setReviewSnapshot(s)}
          unprotected={!isProtected}
        />
        <ChangeHistoryFeed snapshots={snapshots} unprotected={!isProtected} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className={!isProtected ? "border-red-500/20" : undefined}>
          <CardContent className="p-5 text-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${!isProtected ? "bg-red-500/10" : "bg-brand-blue/10"}`}>
              <CalendarDays className={`w-5 h-5 ${!isProtected ? "text-red-500/70" : "text-brand-blue"}`} />
            </div>
            <p className={`text-2xl font-bold ${!isProtected ? "text-red-500" : ""}`} data-testid="text-days-protected">
              {!isProtected ? "0" : (daysProtected > 0 ? daysProtected : 1)}
            </p>
            <p className="text-xs text-muted-foreground">Days Protected</p>
          </CardContent>
        </Card>
        <Card className={!isProtected ? "border-red-500/20" : undefined}>
          <CardContent className="p-5 text-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${!isProtected ? "bg-red-500/10" : "bg-brand-orange/10"}`}>
              <AlertTriangle className={`w-5 h-5 ${!isProtected ? "text-red-500/70" : "text-brand-orange"}`} />
            </div>
            <p className={`text-2xl font-bold ${!isProtected ? "text-red-500" : ""}`} data-testid="text-total-detected">
              {!isProtected ? "?" : snapshots.length}
            </p>
            <p className="text-xs text-muted-foreground">{!isProtected ? "Changes Missed" : "Changes Detected"}</p>
          </CardContent>
        </Card>
        <Card className={!isProtected ? "border-red-500/20" : undefined}>
          <CardContent className="p-5 text-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${!isProtected ? "bg-red-500/10" : "bg-emerald-500/10"}`}>
              <RotateCcw className={`w-5 h-5 ${!isProtected ? "text-red-500/70" : "text-emerald-500"}`} />
            </div>
            <p className={`text-2xl font-bold ${!isProtected ? "text-red-500" : ""}`} data-testid="text-total-restored">
              {!isProtected ? "0" : restoredCount}
            </p>
            <p className="text-xs text-muted-foreground">Changes Restored</p>
          </CardContent>
        </Card>
      </div>

      <ReviewModal snapshot={reviewSnapshot} onClose={() => setReviewSnapshot(null)} />
    </div>
  );
}
